import store from '@/store/vuex'
import { isMobile } from '@/api/utils'
import {
  YOUTUBE_URL_PATTERN,
  TWITCH_PATTERN,
  HOTSPOT_BASE_SIZE
} from '@/api/constants'
import { xmlUrlString } from '@/api/helpers'
import { resizeImage } from '@/utils/resize'

const getMarkerVideoXml = function ({ marker, ath, atv, category, index, markerScale, isMarkerPoint, krpanoXOffset, useCustomIcon }) {
  let rx = 0
  let ry = 0
  let rz = 0
  const depth = 1000
  const distorted = true
  let inverserotation = false

  const videoType = marker.videoplayAttribute.type
  const opacity = marker.opacity ? (marker.opacity / 100) : 0.6
  if (marker.customCoordinate && marker.coordinatePlane !== 0 && marker.rotation) {
    rx = marker.coordinatePlane === 3 ? 90 - atv + (-90 - marker.rotation.x) : atv
    ry = marker.coordinatePlane === 3 ? 0 : -ath + 90 - marker.rotation.y
    rz = marker.rotation.z
    inverserotation = (marker.coordinatePlane === 3)
  }
  const isPilotActive = store.getters.isPilotPlaying
  const isAutoplay = marker.videoplayAttribute.autoplay && !isPilotActive
  const isMuted = marker.videoplayAttribute.mute || !marker.videoplayAttribute.volume
  const isMobileActive = isMobile()
  const userInteraction = {
    mousein: !isMobileActive,
    mouseout: !isMobileActive,
    togglepause: true
  }
  const volume = marker.videoplayAttribute.volume ? marker.videoplayAttribute.volume / 100 : 0
  const onvideoready = isAutoplay ? 'play()' : ''
  const mousein = `marker_mousein(${marker.objectId}, ${index});`
  const mouseout = `marker_mouseout(${marker.objectId}, ${index}, null, ${opacity});`
  const fullscreenVideoplay = `fullscreen_videoplay(${marker.objectId})`
  const onVideoStart = `on_video_start(${marker.objectId})`
  const onVideoplay = `on_video_play(${marker.objectId})`
  const onVideoPaused = `on_video_paused(${marker.objectId})`
  const onVideoComplete = `on_video_complete(${marker.objectId})`
  // const togglepause = 'fullscreen_videoplay();'

  const onout = userInteraction.mouseout ? mouseout : ''
  const onover = userInteraction.mousein ? mousein : ''
  const onclick = (hotspot, onSingleClick, onDoubleClick) => `
    if(
      ${hotspot}.firstclick,
      set(${hotspot}.firstclick, false);
      stopdelayedcall('singleclick');
      ${onDoubleClick}
      break();
    );
    set(${hotspot}.firstclick, true);
    delayedcall('singleclick', 0.4,
      ${onSingleClick}
      set(${hotspot}.firstclick, false);
    )
  `

  const iconUrl = marker.disableResizing ? marker.iconUrl : resizeImage(marker.iconUrl)
  const baseSize = HOTSPOT_BASE_SIZE * 10
  let width = baseSize
  let height = 'prop'

  if (!videoType || videoType === 'upload') {
    // Do not enable loop here, it will manually replay once `onvideocomplete` triggered.
    return `
  <hotspot
    name="markerInfo_${marker.objectId}"
    style="markerInfo"
    scale="0"
    visible="false"
    ath="${ath}"
    atv="${atv - 4.5}"
    alpha="${opacity}"
    zorder="${marker.index}" />

  <hotspot
    url="${!isAutoplay ? xmlUrlString(iconUrl) : ''}"
    name="markerInfo_preview_${marker.objectId}"
    scale="${isAutoplay ? 0 : markerScale}"
    width="${width}"
    height="${height}"
    ath="${ath}"
    atv="${atv}"
    zorder="${marker.index}"
    distorted="true"
    alpha="${opacity}"
    inverserotation="${inverserotation}"
    rx="${rx}"
    ry="${ry}"
    rz="${rz}"
    depth="${depth}"
    mipmapping="true"
    onover="${onover}"
    onout="${onout}"
    enabled="${!marker.disabled}"
    capture="false"
    keep="true"
    renderer="css3d"
    onloaded="hotspot_loaded(${marker.objectId});"
    onclick="${onclick(
    `hotspot[markerInfo_preview_${marker.objectId}]`,
    `set(scale,0);set(visible,false);${onVideoStart};hotspot[marker_${marker.objectId}].play();set(hotspot[marker_${marker.objectId}].scale, ${markerScale});`,
    `set(scale,0);set(muted, true);${fullscreenVideoplay}`
  )}"
  />

  <hotspot
    url.html5="/static/plugins/videoplayer.js"
    videourl="${marker.actionLink}"
    posterurl="${xmlUrlString(iconUrl)}"
    name="marker_${marker.objectId}"
    width="${width}"
    height="${height}"
    scale="${isAutoplay ? markerScale : 0}"
    ath="${ath}"
    atv="${atv}"
    alpha="${opacity}"
    zorder="${marker.index}"
    distorted="${distorted}"
    inverserotation="${inverserotation}"
    rx="${rx}"
    ry="${ry}"
    rz="${rz}"
    depth="${depth}"
    loop="false"
    autoresume="${!isPilotActive}"
    pausedonstart="true"
    enabled="${!marker.disabled}"
    onover="${onover}"
    onout="${onout}"
    onvideoready="${onvideoready}"
    onvideoplay="${onVideoplay}"
    onvideopaused="${onVideoPaused}"
    onvideocomplete="${onVideoComplete}"
    volume="${volume}"
    muted="${isMuted ? 'true' : 'false'}"
    capture="false"
    onclick="${onclick(
    `hotspot[marker_${marker.objectId}]`,
    'togglepause();',
    `set(muted, true);${fullscreenVideoplay}`
  )}"
    />`
  }

  if (videoType === 'embed') {
    height = baseSize
    width = baseSize * 1.77777778 // Force 16:9, fit height
    width = Math.round(width * 10) / 10

    const { embedUrl, platform } = marker.videoplayAttribute
    // IMPORTANT: MUST use '&amp;' instead of '&' in the url
    let iframeUrl = ''
    let youtubeId = ''
    if (platform === 'youtube') {
      if (embedUrl.match(YOUTUBE_URL_PATTERN)) {
        youtubeId = embedUrl.match(YOUTUBE_URL_PATTERN)[1]
      } else {
        return '' // invalid youtube video url
      }
    } else if (platform === 'facebook') {
      iframeUrl = `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(embedUrl)}&amp;show_text=false`
    } else if (platform === 'twitch') {
      const id = embedUrl.match(TWITCH_PATTERN)[1]
      iframeUrl = `https://player.twitch.tv/?channel=${id}&amp;parent=${window.location.host}`
    }
    const baseAtvSubtract = 28
    const atvCalculated = atv - (baseAtvSubtract * markerScale) // responsive value

    return `
  <hotspot
    name="markerInfo_${marker.objectId}"
    style="markerInfo"
    scale="0"
    visible="false"
    ath="${ath}"
    atv="${atvCalculated}"
    alpha="${opacity}"
    zorder="${marker.index}" />
  <hotspot
    name="marker_${marker.objectId}"
    url="https://vrmaker.istaging.com/00000photoUpload/360video_panorama_blank.jpg"
    scale="${markerScale}"
    ath="${ath}"
    atv="${atv}"
    alpha="${opacity}"
    zorder="${marker.index}"
    distorted="${distorted}"
    inverserotation="${inverserotation}"
    rx="${rx}"
    ry="${ry}"
    rz="${rz}"
    depth="${depth}"
    enabled="${!marker.disabled}"
    renderer="css3d"
    onover="${onover}"
    onout="${onout}"
    keep="true"
    onloaded="add_iframe('${iframeUrl}', '${width}', '${height}', ${platform}, ${youtubeId}, ${marker.objectId});"
    />`
  }
}

export default getMarkerVideoXml
